import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Loader2, Pause, Play, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/config/api";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const cadence = (schedule) => {
  const time = `${String(schedule.hour).padStart(2, "0")}:00 UTC`;
  if (schedule.frequency === "daily") return `Every day at ${time}`;
  if (schedule.frequency === "weekly") return `Every ${DAYS[schedule.day_of_week] || "Monday"} at ${time}`;
  return `Day ${schedule.day_of_month} of each month at ${time}`;
};

export const BiSchedules = () => {
  const [schedules, setSchedules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [scheduleRes, historyRes] = await Promise.all([
        axios.get(`${API}/bi/schedules`, { withCredentials: true }),
        axios.get(`${API}/bi/history`, { withCredentials: true }),
      ]);
      setSchedules(scheduleRes.data.schedules || []);
      setRuns(historyRes.data.runs || []);
    } catch (error) {
      console.error("Schedules failed to load", error);
      toast.error("Could not load the scheduled reports");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runNow = async (schedule) => {
    setBusy(schedule.schedule_id);
    try {
      await axios.post(`${API}/bi/schedules/${schedule.schedule_id}/run-now`, {}, { withCredentials: true });
      toast.success("Report generated — see the list below");
      load();
    } catch (error) {
      console.error("Run now failed", error);
      toast.error("Could not generate the report");
    } finally {
      setBusy(null);
    }
  };

  const toggle = async (schedule) => {
    try {
      await axios.patch(
        `${API}/bi/schedules/${schedule.schedule_id}?is_active=${!schedule.is_active}`,
        {},
        { withCredentials: true }
      );
      load();
    } catch (error) {
      console.error("Toggle failed", error);
      toast.error("Could not update that schedule");
    }
  };

  const remove = async (schedule) => {
    try {
      await axios.delete(`${API}/bi/schedules/${schedule.schedule_id}`, { withCredentials: true });
      toast.success("Schedule removed");
      load();
    } catch (error) {
      console.error("Delete failed", error);
      toast.error("Could not remove that schedule");
    }
  };

  const download = async (run) => {
    try {
      const response = await axios.get(`${API}/bi/history/${run.run_id}/download`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${run.template_name.replace(/\s+/g, "-").toLowerCase()}-${run.generated_at.slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed", error);
      toast.error("Could not download that report");
    }
  };

  return (
    <div className="space-y-6" data-testid="bi-schedules">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Scheduled reports</CardTitle>
          <CardDescription>
            Reports run on their own and are kept here to download. Email delivery can be switched on later.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
          ) : schedules.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing scheduled yet — open the Templates tab and press Schedule on any report.
            </p>
          ) : (
            <div className="space-y-2">
              {schedules.map((schedule) => (
                <div
                  key={schedule.schedule_id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-lg border"
                  data-testid={`bi-schedule-${schedule.schedule_id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{schedule.template_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {cadence(schedule)} · next run {schedule.next_run_at ? schedule.next_run_at.slice(0, 16).replace("T", " ") : "—"}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="text-[10px]">
                      {schedule.is_active ? "Active" : "Paused"}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      disabled={busy === schedule.schedule_id}
                      onClick={() => runNow(schedule)}
                      data-testid={`bi-schedule-runnow-${schedule.schedule_id}`}
                    >
                      {busy === schedule.schedule_id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Play className="h-3.5 w-3.5" />
                      )}
                      Run now
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggle(schedule)}
                      data-testid={`bi-schedule-toggle-${schedule.schedule_id}`}
                    >
                      {schedule.is_active ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(schedule)}
                      data-testid={`bi-schedule-delete-${schedule.schedule_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Generated reports</CardTitle>
          <CardDescription>Every run is kept with its data — download it as a formatted Excel file.</CardDescription>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No reports generated yet.</p>
          ) : (
            <div className="space-y-2">
              {runs.map((run) => (
                <div
                  key={run.run_id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg border"
                  data-testid={`bi-run-${run.run_id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{run.template_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {run.generated_at.slice(0, 16).replace("T", " ")} · {run.row_count} row(s) ·{" "}
                      {run.triggered_by === "schedule" ? "automatic" : `by ${run.triggered_by}`}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5 shrink-0"
                    onClick={() => download(run)}
                    data-testid={`bi-run-download-${run.run_id}`}
                  >
                    <Download className="h-4 w-4" />
                    Excel
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BiSchedules;
